import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Wallet, CalendarDays, Briefcase, Plus, Save } from 'lucide-react';
import { toast, Toaster } from 'sonner';

interface TourWidget {
  id: string;
  user_id: string;
  price_from: string | null;
  price_to: string | null;
  destination: string | null;
  date_start: string | null;
  date_end: string | null;
  comment: string | null;
  created_at: string;
}

export default function TourWidgetPopup() {
  const [user, setUser] = useState<any>(null);
  const [widgets, setWidgets] = useState<TourWidget[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [widgetData, setWidgetData] = useState({
    priceFrom: '',
    priceTo: '',
    destination: '',
    dateStart: '',
    dateEnd: '',
    comment: '',
  });

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      setLoading(false);
    };
    getUser();
  }, []);

  useEffect(() => {
    if (user) {
      loadWidgets();
    }
  }, [user]);

  const loadWidgets = async () => {
    const { data } = await supabase
      .from('tour_widgets')
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });
    if (data) {
      setWidgets(data);
    }
  };

  const handleWidgetChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setWidgetData({ ...widgetData, [e.target.name]: e.target.value });
  };

  const clearAll = () => {
    setWidgetData({ priceFrom: '', priceTo: '', destination: '', dateStart: '', dateEnd: '', comment: '' });
  };

  const saveWidget = async () => {
    const priceFrom = Number(widgetData.priceFrom) || 0;
    const priceTo = Number(widgetData.priceTo) || 0;

    if (priceFrom && priceTo && priceFrom >= priceTo) {
      toast.error('Цена "От" должна быть меньше цены "До"');
      return;
    }

    if (!widgetData.destination && !widgetData.priceFrom) {
      toast.error('Заполните хотя бы направление или цену');
      return;
    }

    await supabase.from('tour_widgets').insert({
      user_id: user?.id,
      price_from: widgetData.priceFrom || null,
      price_to: widgetData.priceTo || null,
      destination: widgetData.destination || null,
      date_start: widgetData.dateStart || null,
      date_end: widgetData.dateEnd || null,
      comment: widgetData.comment || null,
    });

    clearAll();
    setIsCollapsed(true);
    loadWidgets();
    toast.success('Вариант сохранен');
  };

  const deleteWidget = async (id: string) => {
    await supabase.from('tour_widgets').delete().eq('id', id);
    loadWidgets();
    toast.success('Вариант удален');
  };

  const formatNumber = (num: string | null) => {
    if (!num) return '0';
    return Number(num).toLocaleString('ru-RU');
  };

  const handleClose = () => {
    // Save current form data before closing
    if (widgetData.destination || widgetData.priceFrom) {
      saveWidget().then(() => {
        window.close();
      });
    } else {
      window.close();
    }
  };

  const priceFrom = Number(widgetData.priceFrom) || 0;
  const priceTo = Number(widgetData.priceTo) || 0;
  const isPriceError = widgetData.priceFrom && widgetData.priceTo && priceFrom >= priceTo;
  const canSave = !isPriceError && (widgetData.destination || (widgetData.priceFrom && widgetData.priceTo));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center text-slate-500 dark:text-slate-400">
          <p>Для использования виджета необходимо войти в систему</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="p-4 border-b flex justify-between items-center bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <Briefcase size={18} />
          <span className="font-bold">Быстрый подбор</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearAll} className="text-xs text-slate-400 hover:text-red-500 transition-colors font-medium px-2 py-1">
            Очистить всё
          </button>
          <button 
            onClick={handleClose}
            title="Сохранить и закрыть"
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Save size={14} />
            Закрыть
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4 overflow-y-auto flex-1">
        {!isCollapsed ? (
          <>
            {widgets.length > 0 && (
              <div className="flex justify-end mb-2">
                <button onClick={() => setIsCollapsed(true)} className="text-slate-400 hover:text-slate-600">
                  <X size={16} />
                </button>
              </div>
            )}

            {isPriceError && (
              <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm font-medium dark:bg-red-900/30 dark:border-red-700">
                Ошибка: Цена "От" должна быть меньше цены "До".
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Цена от</label>
                <input
                  type="number"
                  name="priceFrom"
                  value={widgetData.priceFrom}
                  onChange={handleWidgetChange}
                  className={`w-full rounded-xl px-3 py-2.5 text-sm font-bold focus:outline-none border ${
                    isPriceError
                      ? 'border-red-500'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                  }`}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Цена до</label>
                <input
                  type="number"
                  name="priceTo"
                  value={widgetData.priceTo}
                  onChange={handleWidgetChange}
                  className={`w-full rounded-xl px-3 py-2.5 text-sm font-bold focus:outline-none border ${
                    isPriceError
                      ? 'border-red-500'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Направление</label>
              <input
                name="destination"
                value={widgetData.destination}
                onChange={handleWidgetChange}
                className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Дата вылета</label>
                <input
                  type="date"
                  name="dateStart"
                  value={widgetData.dateStart}
                  onChange={handleWidgetChange}
                  className="w-full rounded-xl px-3 py-2.5 text-sm font-bold focus:outline-none border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Дата возврата</label>
                <input
                  type="date"
                  name="dateEnd"
                  value={widgetData.dateEnd}
                  onChange={handleWidgetChange}
                  className="w-full rounded-xl px-3 py-2.5 text-sm font-bold focus:outline-none border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 ml-1 mb-1 block">Комментарий</label>
              <textarea
                name="comment"
                value={widgetData.comment}
                onChange={handleWidgetChange}
                className="w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none border resize-none h-20 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                placeholder="Пожелания..."
              />
            </div>

            <button
              onClick={saveWidget}
              disabled={!canSave}
              className={`w-full py-3 rounded-xl font-semibold shadow-lg transition-all ${
                canSave ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800' : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }`}
            >
              Сохранить вариант
            </button>
          </>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={() => setIsCollapsed(false)}
              className="p-2 rounded-full bg-blue-50 hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-600"
            >
              <Plus size={20} />
            </button>
          </div>
        )}

        {widgets.length > 0 && (
          <div className="mt-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <h4 className="text-xs font-bold uppercase text-slate-400 mb-3">Сохраненные варианты</h4>
            <div className="space-y-3">
              {widgets.map(w => (
                <div
                  key={w.id}
                  className="p-4 rounded-xl border text-sm relative group bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                >
                  <div className="font-bold text-base mb-1 text-blue-500">{w.destination || 'Без направления'}</div>
                  <div className="flex flex-col gap-1 text-xs opacity-80">
                    {w.price_from && w.price_to && (
                      <div className="flex items-center">
                        <Wallet size={12} className="inline mr-1" />
                        <span>{formatNumber(w.price_from)}</span>
                        <span className="mx-2 text-slate-400">—</span>
                        <span>{formatNumber(w.price_to)}</span>
                      </div>
                    )}
                    {w.date_start && w.date_end && (
                      <div>
                        <CalendarDays size={12} className="inline mr-1" />
                        {w.date_start} → {w.date_end}
                      </div>
                    )}
                    {w.comment && <div className="mt-1 italic opacity-70">{w.comment}</div>}
                  </div>
                  <button
                    onClick={() => deleteWidget(w.id)}
                    className="absolute top-3 right-3 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
